import { useCallback, useRef, useState } from 'react';
import { DetectionPogPairing } from '../pages/tasks/components/Audit';
import { ApiService } from '../services/api';
import { ProductVariantResponse } from '../types';

const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

function getCacheKey(storeName: string, upc: string): string {
  return `gt-variants-${storeName}-${upc}`;
}

function readCache(storeName: string, upc: string): ProductVariantResponse[] | null {
  try {
    const raw = sessionStorage.getItem(getCacheKey(storeName, upc));
    if (!raw) return null;
    const cached = JSON.parse(raw) as { data: ProductVariantResponse[]; timestamp: number };
    if (Date.now() - cached.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(getCacheKey(storeName, upc));
      return null;
    }
    return cached.data;
  } catch {
    return null;
  }
}

function writeCache(storeName: string, upc: string, data: ProductVariantResponse[]): void {
  try {
    sessionStorage.setItem(
      getCacheKey(storeName, upc),
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

export function useVariantSelection() {
  const [variantsByUpc, setVariantsByUpc] = useState<Map<string, ProductVariantResponse[]>>(
    new Map()
  );
  const [selectedVariants, setSelectedVariants] = useState<Map<string, ProductVariantResponse>>(
    new Map()
  );
  const [loadingUpcs, setLoadingUpcs] = useState<Set<string>>(new Set());

  // In-flight promise deduplication
  const inFlightRef = useRef<Map<string, Promise<ProductVariantResponse[]>>>(new Map());

  const fetchVariantsForUpc = useCallback(
    async (token: string, storeName: string, upc: string): Promise<void> => {
      // Already have data in state
      if (variantsByUpc.has(upc)) return;

      // Check sessionStorage cache
      const cached = readCache(storeName, upc);
      if (cached) {
        setVariantsByUpc((prev) => {
          const next = new Map(prev);
          next.set(upc, cached);
          return next;
        });
        return;
      }

      // Deduplicate in-flight requests
      const existingPromise = inFlightRef.current.get(upc);
      if (existingPromise) {
        await existingPromise;
        return;
      }

      setLoadingUpcs((prev) => {
        const next = new Set(prev);
        next.add(upc);
        return next;
      });

      const promise = ApiService.fetchVariants(token, storeName, upc);
      inFlightRef.current.set(upc, promise);

      try {
        const data = await promise;
        writeCache(storeName, upc, data);
        setVariantsByUpc((prev) => {
          const next = new Map(prev);
          next.set(upc, data);
          return next;
        });
      } finally {
        inFlightRef.current.delete(upc);
        setLoadingUpcs((prev) => {
          const next = new Set(prev);
          next.delete(upc);
          return next;
        });
      }
    },
    [variantsByUpc]
  );

  const selectVariant = useCallback(
    (detectionKey: string, variant: ProductVariantResponse): void => {
      setSelectedVariants((prev) => {
        const next = new Map(prev);
        next.set(detectionKey, variant);
        return next;
      });
    },
    []
  );

  const clearVariant = useCallback((detectionKey: string): void => {
    setSelectedVariants((prev) => {
      const next = new Map(prev);
      next.delete(detectionKey);
      return next;
    });
  }, []);

  const getSelectedVariant = useCallback(
    (detectionKey: string): ProductVariantResponse | undefined => {
      return selectedVariants.get(detectionKey);
    },
    [selectedVariants]
  );

  const getVariantsForUpc = useCallback(
    (upc: string): ProductVariantResponse[] | undefined => {
      return variantsByUpc.get(upc);
    },
    [variantsByUpc]
  );

  const allVariantsSelected = useCallback(
    (pairings: Map<string, DetectionPogPairing>): boolean => {
      for (const [detectionKey, pairing] of pairings) {
        if (!pairing.upc) continue;
        const variants = variantsByUpc.get(pairing.upc);
        if (variants && variants.length > 1 && !selectedVariants.has(detectionKey)) {
          return false;
        }
      }
      return true;
    },
    [variantsByUpc, selectedVariants]
  );

  return {
    variantsByUpc,
    selectedVariants,
    loadingUpcs,
    fetchVariantsForUpc,
    selectVariant,
    clearVariant,
    getSelectedVariant,
    getVariantsForUpc,
    allVariantsSelected,
  };
}
